import React, { useEffect, useState, useRef } from "react";
import { Tooltip, Input, Tag, theme, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { TweenOneGroup } from "rc-tween-one";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { editProductApi } from "../../services/products.service";
import Highlighter from "react-highlight-words";
import UpgradeDialog from "../../components/common/UpgradeDialog";

export default function TagsEditor({ row, value = [], searchQuery, onChange }) {
  const { token } = theme.useToken();
  const [tags, setTags] = useState(value);
  const [adding, setAdding] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [inputVisible, setInputVisible] = useState(false); // for "add" input
  const [editingIndex, setEditingIndex] = useState(null); // which tag is being edited
  const [inputValue, setInputValue] = useState("");
  const [editInputWidth, setEditInputWidth] = useState(160);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);
  const dispatch = useAppDispatch();
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);

  useEffect(() => {
    // Avoid overriding local edit state while the user is actively editing/adding.
    if (editingIndex !== null || inputVisible || adding || inFlight) return;
    setTags(Array.isArray(value) ? value : []);
  }, [value, editingIndex, inputVisible, adding, inFlight]);

  // focus the input after it mounts (use timeout so DOM is ready)
  useEffect(() => {
    if (inputVisible || editingIndex !== null) {
      // next tick focus (ensures inputRef is attached)
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [inputVisible, editingIndex]);

  // outside click: when editing, clicking anywhere that's NOT the input and NOT a tag element should cancel (discard).
  useEffect(() => {
    if (editingIndex === null) return;

    const handleDocClick = (e) => {
      const target = e.target;
      // if click is inside the input, do nothing
      if (inputContainerRef.current && inputContainerRef.current.contains(target)) return;
      // if click is on a tag (we mark tag containers with data-tag="true"), do nothing
      if (target && target.closest && target.closest("[data-tag='true']")) return;
      // otherwise discard edit
      cancelEdit();
    };

    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingIndex]);

  const saveTags = (tagsToSave, { onSuccess, onFailure, onFinally } = {}) => {
    setInFlight(true);
    dispatch(
      editProductApi({
        params: {
          product_id: row.product_id || row._id,
          product_code: row.code,
          product_name: row.name,
          tags: tagsToSave.join(","),
        },
        finalCallback: () => {
          setInFlight(false);
          onFinally?.();
        },
        successCallback: () => {
          message.success(<>Tags updated successfully for <b>{row.name}</b></>);
          onSuccess?.();
        },
        failureCallback: (errors) => {
          console.error("Failed to update tags", errors);
          message.error(<>Failed to update tags for <b>{row.name}</b></>);
          onFailure?.(errors);
        },
      })
    );
  };

  const showAddInput = () => {
    if (inFlight || adding) return;
    if (!isPaidPlan) {
      setShowUpgradeDialog(true);
      return;
    }
    setInputVisible(true);
    setInputValue("");
    setEditingIndex(null);
  };

  const handleAddOrSave = () => {
    const trimmed = inputValue?.trim();
    if (!trimmed) {
      // empty => cancel
      if (editingIndex !== null) cancelEdit();
      else {
        setInputVisible(false);
        setInputValue("");
      }
      return;
    }

    let newTags;
    if (editingIndex !== null) {
      newTags = [...tags];
      newTags[editingIndex] = trimmed;
    } else {
      if (tags.includes(trimmed)) {
        message.warning(<>Tag already exists for <b>{row.name}</b></>);
        return;
      }
      newTags = [...tags, trimmed];
    }

    setAdding(true);

    saveTags(newTags, {
      onSuccess: () => {
        setTags(newTags);
        onChange?.(newTags.join(","));
        setInputValue("");
        setInputVisible(false);
        setEditingIndex(null);
      },
      onFailure: () => {
        // keep edit open for retry
      },
      onFinally: () => setAdding(false),
    });
  };

  const cancelEdit = () => {
    // discard changes, restore UI
    setInputValue("");
    setInputVisible(false);
    setEditingIndex(null);
  };

  const handleClose = (removedTag) => {
    if (inFlight) return;
    const newTags = tags.filter((t) => t !== removedTag);
    saveTags(newTags, {
      onSuccess: () => {
        setTags(newTags);
        onChange?.(newTags.join(","));
      },
    });
  };

  const handleEdit = (tag, index, e) => {
    e.stopPropagation();
    if (inFlight || adding) return;
    if (!isPaidPlan) {
      setShowUpgradeDialog(true);
      return;
    }
    const tagElement = e.currentTarget?.closest?.(".ant-tag");
    if (tagElement) {
      const width = Math.max(80, Math.ceil(tagElement.getBoundingClientRect().width));
      setEditInputWidth(width);
    } else {
      setEditInputWidth(160);
    }
    setEditingIndex(index);
    setInputValue(tag);
    setInputVisible(false);
  };

  const forMap = (tag, index) => {
    const tagKey = `tag-${index}-${tag}`;
    // when editing this tag, render an Input with a unique key so React mounts it fresh
    if (editingIndex === index) {
      return (
        <div
          key={tagKey}
          style={{ display: "inline-block", marginBottom: 4 }}
          ref={inputContainerRef}
        >
          <Input
            ref={inputRef}
            size="small"
            style={{ width: editInputWidth }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={handleAddOrSave}
            // clicking outside will be detected by document click handler; blur alone is unreliable in some nested components — keep blur to also discard
            onBlur={() => {
              // don't immediately cancel here; let document click handler handle outside click.
              // But also cancel on blur if neither inputVisible nor editingIndex changed after a short delay.
              setTimeout(() => {
                if (
                  editingIndex !== null &&
                  inputContainerRef.current &&
                  !inputContainerRef.current.contains(document.activeElement)
                ) {
                  cancelEdit();
                }
              }, 0);
            }}
            disabled={adding || inFlight}
            // stop propagation so our document click handler doesn't see the same click as "outside"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    const displayText = tag.length > 35 ? `${tag.slice(0, 35)}...` : tag;

    return (
      <span
        key={tagKey}
        style={{ display: "inline-block", marginBottom: 4 }}
        // mark this span so document click handler can detect clicks on tags and avoid cancelling edit
        data-tag="true"
      >
        <Tooltip title={tag}>
          <Tag
            closable
            onClose={(e) => {
              e.preventDefault();
              handleClose(tag);
            }}
            style={{ cursor: "pointer" }}
          >
            <span
              onDoubleClick={(e) => handleEdit(tag, index, e)}
              // also mark inner span as a tag target
              data-tag="true"
            >
              <Highlighter
                highlightClassName="YourHighlightClass"
                searchWords={[searchQuery]}
                autoEscape={true}
                textToHighlight={displayText}
                highlightStyle={{
                  backgroundColor: "#FFD700",
                  padding: "2px 0",
                  borderRadius: "4px",
                  paddingLeft: "2px",
                  paddingRight: "2px",
                }}
              />
            </span>
          </Tag>
        </Tooltip>
      </span>
    );
  };

  const tagPlusStyle = {
    background: token.colorBgContainer,
    borderStyle: "dashed",
    cursor: adding || inFlight ? "not-allowed" : "pointer",
    opacity: adding || inFlight ? 0.6 : 1,
    width: 22,
    height: 22,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "22px",
    fontSize: 12,
  };

  const shouldAnimate = editingIndex === null && !inputVisible;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {shouldAnimate ? (
        <TweenOneGroup
          appear={false}
          enter={{ opacity: 1, scale: 1, duration: 0 }}
          leave={{ opacity: 0, width: 0, scale: 0, duration: 200 }}
        >
          {tags.map((tag, index) => forMap(tag, index))}
        </TweenOneGroup>
      ) : (
        tags.map((tag, index) => forMap(tag, index))
      )}

      {/* add input */}
      {!inputVisible && editingIndex === null && (
        <Tooltip title={inFlight ? "Request in progress" : "Add Tag"}>
          <Tag onClick={showAddInput} style={tagPlusStyle}>
            <PlusOutlined />
          </Tag>
        </Tooltip>
      )}

      {inputVisible && editingIndex === null && (
        <div ref={inputContainerRef}>
          <Input
            ref={inputRef}
            type="text"
            size="small"
            style={{ width: 160 }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={handleAddOrSave}
            onBlur={() => {
              // cancel add when clicking away
              setTimeout(() => {
                if (
                  !inputContainerRef.current ||
                  !inputContainerRef.current.contains(document.activeElement)
                ) {
                  setInputVisible(false);
                  setInputValue("");
                }
              }, 0);
            }}
            disabled={adding || inFlight}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <UpgradeDialog
        visible={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature="Products and Editing product tags"
      />
    </div>
  );
}
